import { useCallback, useMemo, useState } from "react"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import Box from "@mui/material/Box"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import fp from "lodash/fp"

import type { Theme } from "@mui/material"

import type { DeviceWGroupsNStatesNStatus } from "types/device.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useAddDeviceToGroupMutation } from "features/api"
import LoadingButton from "widgets/common/LoadingButton"
import PopUp from "widgets/common/PopUp"
import Spinner from "widgets/common/Spinner"
import useDevices from "helpers/hooks/useDevices"
import AddDevicesToGroupTable from "./AddDevicesToGroupTable"

interface AddDevicesToGroupPopupProps {
  open: boolean
  onClose: () => void
  groupDevices: DeviceWGroupsNStatesNStatus[]
}

const AddDevicesToGroupPopup = ({
  onClose,
  open,
  groupDevices,
}: AddDevicesToGroupPopupProps) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const orgId = useSelector(selectCurrentOrgId)
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<number[]>([])
  const {
    devsWRelsById,
    isFetching: devicesFetching,
    isLoading: devicesLoading,
  } = useDevices()
  const devicesFirstFetch = useMemo(
    () => devicesFetching && fp.isEmpty(devsWRelsById),
    [devsWRelsById, devicesFetching],
  )

  const devicesToAdd = useMemo(() => {
    const groupDevicesIds = fp.map((groupDevices: DeviceWGroupsNStatesNStatus) =>
      groupDevices.device.less_id.toString(),
    )(groupDevices)
    const devicesNotInGroup = fp.values(
      fp.pickBy((_value, key) => !groupDevicesIds.includes(key), devsWRelsById),
    )
    return devicesNotInGroup
  }, [devsWRelsById, groupDevices])

  const [addDeviceToGroup, { isLoading: addDeviceIsLoading }] =
    useAddDeviceToGroupMutation()

  const addButtonDisabled = useMemo(
    () => !(selectedDeviceIds && selectedDeviceIds.length > 0),
    [selectedDeviceIds],
  )

  const handleAddDevices = useCallback(async () => {
    if (selectedDeviceIds && selectedDeviceIds.length > 0) {
      for (const selectedDeviceId of selectedDeviceIds) {
        await addDeviceToGroup({
          less_id: selectedDeviceId,
          org_id: orgId as number,
          group_id: id as string,
        })
      }
      onClose()
    }
  }, [selectedDeviceIds, addDeviceToGroup, orgId, id, onClose])

  const isLoadingDevices = devicesFirstFetch || devicesLoading
  const hasDevicesToAdd = devicesToAdd?.length > 0

  const actions = (
    <>
      <Button
        onClick={onClose}
        sx={{
          color: (theme: Theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.grey[300]
              : theme.palette.grey[600],
        }}
      >
        {t("generic.CANCEL")}
      </Button>
      <LoadingButton
        styles={{ width: "fit-content" }}
        loading={addDeviceIsLoading}
        onClick={handleAddDevices}
        disabled={addButtonDisabled}
        type="button"
      >
        {t("generic.ADD")}
      </LoadingButton>
    </>
  )

  return (
    <PopUp
      open={open}
      onClose={onClose}
      titleKey="device_groups.ADD_DEVICE"
      actions={isLoadingDevices ? undefined : actions}
      disableGutters={!isLoadingDevices && hasDevicesToAdd}
      maxWidth="md"
      fullWidth
      // Keeps the grid at its full height, so the content scrolls instead of cutting off rows.
      sx={{ "& .MuiDataGrid-root": { flexShrink: 0 } }}
    >
      {isLoadingDevices ? (
        <Box
          sx={{
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner />
        </Box>
      ) : hasDevicesToAdd ? (
        <AddDevicesToGroupTable
          devices={devicesToAdd}
          selectedIds={selectedDeviceIds}
          onSelectionChange={setSelectedDeviceIds}
        />
      ) : (
        <Typography>{t("device.NO_DEVICES_ASSIGN")}</Typography>
      )}
    </PopUp>
  )
}

export default AddDevicesToGroupPopup
